import { api, API_URL } from './client.js';
import { auth } from '../auth/state.js';

export const listLayers   = ()         => api.get('/layers');
export const getLayer     = (id)       => api.get(`/layers/${id}`);
export const getFeatures  = (id, opts = {}) => {
  const limit  = opts.limit  ?? 5000;
  const offset = opts.offset ?? 0;
  return api.get(`/layers/${id}/features?limit=${limit}&offset=${offset}`);
};
export const deleteLayer  = (id)       => api.del(`/layers/${id}`);
export const shareLayer   = (id)       => api.post(`/layers/${id}/share`);
export const unshareLayer = (id)       => api.post(`/layers/${id}/unshare`);
export const updateFeature = (layerId, fid, properties) =>
  api.patch(`/layers/${layerId}/features/${fid}`, { properties });

/** All public layers visible to the current user (any owner). */
export const listCatalog  = (opts = {}) => {
  const params = new URLSearchParams();
  if (opts.category) params.set('category', opts.category);
  if (opts.limit) params.set('limit', String(opts.limit));
  const q = params.toString();
  return api.get(`/catalog${q ? `?${q}` : ''}`);
};

/**
 * Multipart upload with progress events. `fetch` doesn't expose the upload
 * progress, so we use XHR.
 *
 * Returns `{ promise, cancel }` so callers can abort an in-flight upload —
 * `cancel()` calls `xhr.abort()` which rejects the promise with
 * "Upload cancelled". The Layer row that the backend already created from
 * the multipart preamble may linger in `processing` state; the caller can
 * delete it server-side if they care.
 *
 * @param {object} args
 * @param {File}   args.file
 * @param {string} args.name
 * @param {string} [args.category]
 * @param {string} [args.description]
 * @param {(pct:number)=>void} [args.onProgress]   0..100, or -1 for "indeterminate"
 * @returns {{ promise: Promise<object>, cancel: () => void }}
 */
export function uploadLayer({ file, name, category, description, onProgress }) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('name', name);
  if (category)    fd.append('category', category);
  if (description) fd.append('description', description);

  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${API_URL}/layers/upload`);
  const token = auth.getToken();
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

  const promise = new Promise((resolve, reject) => {
    xhr.upload.addEventListener('progress', e => {
      if (!onProgress) return;
      onProgress(e.lengthComputable ? (e.loaded / e.total) * 100 : -1);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error('Bad server response')); }
      } else {
        let detail;
        try { detail = JSON.parse(xhr.responseText)?.detail; } catch {}
        reject(new Error(detail || `Upload failed (HTTP ${xhr.status})`));
      }
    });
    xhr.addEventListener('error',   () => reject(new Error("Can't reach the server.")));
    xhr.addEventListener('abort',   () => reject(Object.assign(new Error('Upload cancelled'), { aborted: true })));
    xhr.addEventListener('timeout', () => reject(new Error('Upload timed out')));

    xhr.send(fd);
  });

  return { promise, cancel: () => xhr.abort() };
}

export async function pollUntilReady(id, { timeoutMs = 60_000, intervalMs = 800 } = {}) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const layer = await getLayer(id);
    if (layer.status === 'ready')  return layer;
    if (layer.status === 'failed') throw new Error(layer.status_message || 'Ingestion failed');
    await new Promise(r => setTimeout(r, intervalMs));
  }
  throw new Error('Timed out waiting for ingestion');
}
